<template>
  <div class='wrapper-page'>
    <folder-settings ref='folderSettings'/>
    <share-folder-form @success='loadFolders()' ref='shareFolderForm'/>
    <bulk-share-folder-form ref='bulkShareFolderForm' @success='loadFolders()' />
    <bulk-subscription-form ref='bulkSubscribeFolderForm' @success='loadFolders()' />
    <subscribe-to-folder-form @reload-callback='loadFolders()' ref='subscribeFolderForm'/>
    <folder-form @success='loadFolders()' ref='folderForm'/>
    <delete-folder-form @success='loadFolders()' ref='deleteFolderForm'/>
    <contact-form @success='loadContact()' ref='contactForm'/>
    <upload-to-folder-form @success='loadFolders()' ref='uploadForm'/>

    <div class='container-fluid' id='contact-details'>
      <h1 class='page-title'>
        {{ contact.name }}

        <a @click.prevent='$refs.contactForm.open(contact)' class='edit' href='#'>
          {{ $t('contacts.show.edit') }}
        </a>
      </h1>

      <div class='contact-info'>
        <div class="company-details">
          <h5>
            {{ $t('contacts.show.company') }}
            <strong v-if='contact.company'>
              {{ contact.company }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_company_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.position') }}
            <strong v-if='contact.position'>
              {{ contact.position }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_position_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.company_address') }}
            <strong v-if='contact.address'>
              {{ contact.address }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_company_address_added') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.expired_files_link') }}

            <div class="copy-to-clipboard-wrapper">
              <input type="text" name="public_link" id="public_link" :value="contactPageUrl" disabled="disabled" class="public_link input-link-to-clipboard">

              <a href="" class="btn btn-outline-primary float-right copy-link-to-clipboard" @click.prevent="copyToClipboard($event, '.input-link-to-clipboard')">
                <i class="fas fa-link"></i>
                {{ $t('folder_show.settings.upload_link_button') }}
              </a>
            </div>
          </h5>
        </div>
        <div class="contact-details">
          <h5>
            {{ $t('contacts.show.email') }}
            <strong v-if='contact.email'>
              {{ contact.email }}
            </strong>
            <strong class='red-text' v-else>
              {{ $t('contacts.show.no_email') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.language') }}
            <strong v-if="contact.locale === 'de'">
              {{ $t('contacts.edit_modal.german') }}
            </strong>
            <strong v-else>
              {{ $t('contacts.edit_modal.english') }}
            </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.notes') }}
            <strong v-if='contact.notes'>
              {{ contact.notes }}
            </strong>
            <strong v-else>
              {{ $t('contacts.show.no_notes_added') }}
           </strong>
          </h5>
          <h5>
            {{ $t('contacts.show.communication_status') }}
            <router-link
              :data-tooltip='lastMessageAlt'
              :to="{name: 'contact_communications', params: { id: id }}"
              class='own-tooltip own-tooltip-right'>
              <img :src='lastMessageSrc'/>
            </router-link>
          </h5>
          <h5>
            {{ $t('contacts.show.files') }}
            <strong>{{ contactStats.records_count }}</strong>
          </h5>
        </div>

        <div class='contact-tags'>
          <h5 class='tags-dl'>
            {{ $t('contacts.show.tags') }}
          </h5>

          <div class='tags-cont'>
            <tag-manager :resource='contact' resourceType='contact'
                         v-on:reload-list='loadContact()'/>

            <div :class='tag.color' class='existing-tags-wrapper'
                 v-for='tag in contact.tags'>
              <div class='btn-group btn-group-toggle' data-toggle='buttons'>
                <label class='btn active'>
                  <router-link :to="{name: 'tag', params: { id: tag.id }}">
                    {{ tag.name }}
                  </router-link>
                </label>

                <label @click.prevent='removeConnectedTag(tag)' class='btn'>
                  <i class='fa fa-remove'></i>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="float-left checklist-status">
          <h5>
            {{ $t('contacts.show.checklist_status') }}
          </h5>
          <checklist-status :completed-items-count='contactStats.checked_checklist_items' :items-count='contactStats.total_checklist_items'/>
        </div>
      </div>

      <a @click.prevent="$refs.folderForm.open({ contact_id: contact.id })"
         class="btn btn-outline-primary float-right"
         href="#">
        {{ $t('folders.list.add_folder') }}
      </a>
      <div class='nav-tabs-wrapper'>
        <ul class='nav nav-tabs' role='tablist'>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_folders', params: { id: id } }"
                         class="nav-link show active">
              {{ $t('contacts.show.folders') }}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_communications', params: { id: id } }"
                         class="nav-link show">
              {{ $t('contacts.show.communication') }}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_interactive_notes', params: { id: id } }" class="nav-link show">
              {{$t('contacts.show.interactive_notes')}}
            </router-link>
          </li>
          <li class='nav-item'>
            <router-link :to="{ name: 'contact_emails', params: { id: id } }"
                         class="nav-link show">
              {{ $t('contacts.show.automated_emails') }}
            </router-link>
          </li>
        </ul>
      </div>

      <div class='tab-content'>
        <div class="tab-pane active text-center">
          <loading v-if="loading"/>

          <div id='folders-list' v-else>

            <div class="btn-group float-right show-hide-columns" role="group"
                 v-click-outside="closeShowColumnsDropdown">
              <button @click.prevent="showColumnsDropdown = !showColumnsDropdown"
                      class="btn btn-neutral dropdown-toggle">
                {{ $t('records_list.show_hide') }}
              </button>

              <ul class="dropdown-menu" v-show="showColumnsDropdown">
                <li class="dropdown-item" @click="toggleColumns('name')">
                  <input type="checkbox" :checked="enabledColumns['name']" />
                  {{ $t('records_list.table.name') }}
                </li>
                <li class="dropdown-item" @click="toggleColumns('shared')">
                  <input type="checkbox" :checked="enabledColumns['shared']" />
                  {{ $t('records_list.table.shared') }}
                </li>
                <li class="dropdown-item" @click="toggleColumns('completeness')">
                  <input type="checkbox" :checked="enabledColumns['completeness']" />
                  {{ $t('folders.list.checklist_status') }}
                </li>
                <li class="dropdown-item" @click="toggleColumns('subscribed')">
                  <input type="checkbox" :checked="enabledColumns['subscribed']" />
                  {{ $t('records_list.table.subscribed') }}
                </li>
                <li class="dropdown-item" @click="toggleColumns('tags')">
                  <input type="checkbox" :checked="enabledColumns['tags']" />
                  {{ $t('records_list.table.tags') }}
                </li>
                <li class="dropdown-item" @click="toggleColumns('noFilesFolders')">
                  <input type="checkbox" :checked="enabledColumns['noFilesFolders']" />
                  {{ $t('records_list.table.no_files_folder') }}
                </li>
                <li class="dropdown-item" @click="toggleColumns('created_at')">
                  <input type="checkbox" :checked="enabledColumns['created_at']" />
                  {{ $t('folders.list.created_at') }}
                </li>
                <li class="dropdown-item" @click="toggleColumns('actions')">
                  <input type="checkbox" :checked="enabledColumns['actions']" />
                  {{ $t('records_list.table.actions') }}
                </li>
                <li>
                  <button @click="closeShowColumnsDropdown"
                          class="btn btn-primary float-right m-r-10">
                    {{ $t('records_list.table.save') }}
                  </button>
                </li>
              </ul>
            </div>

            <filter-list />

            <filter-list-from-params />

            <div class="empty-list-wrapper" v-if='hasFilters && folders.length === 0'>
              <img src="../assets/empty_states/folders.png" alt="files"/>
              <p>{{ $t('root.no_data_filters') }}</p>
              <router-link :to='{ query: {} }'>
                {{ $t('root.clear_filters') }}
              </router-link>
            </div>

            <div v-else-if="folders.length === 0">
              <div class="empty-list-wrapper">
                <p>{{ $t('contacts.show.no_folders_msg') }}</p>
                <a @click.prevent="$refs.folderForm.open({ contact_id: contact.id })"
                   class="btn btn-outline-primary contact-no-folders" href="#">
                  {{ $t('folders.list.add_folder') }}
                </a>
              </div>
            </div>

            <div class="table_wrapper" v-else>
              <div :class="['bulk-container', { 'hide': bulkOpFolders.length === 0  }]">
                <span>{{ bulkOpFolders.length }} {{ $t('root.folders') }}</span>
                <ul>
                  <li>
                    <a @click.prevent="$refs.bulkShareFolderForm.open(bulkOpFolders)" class="dropdown-item" href="#">
                      <i class="fas fa-share-alt"></i>
                      {{ $t('share_invite.share_folder') }}
                    </a>
                  </li>
                  <li>
                    <a @click.prevent="$refs.bulkTagItemsModal.open(bulkOpFolders)" href="#" class="dropdown-item">
                      <i class="fas fa-tag"></i>
                      {{ $t('folder_show.add_tags') }}
                    </a>
                    <bulk-tag-items-modal ref='bulkTagItemsModal' @success='loadFolders()' />
                  </li>
                  <li>
                    <a @click.prevent="$refs.bulkSubscribeFolderForm.open(bulkOpFolders)" href="#" class="dropdown-item">
                      <i class="fas fa-tag"></i>
                      {{ $t('folder_show.subscribe') }}
                    </a>
                  </li>
                </ul>
              </div>
              <table class="table">
                <thead>
                <tr>
                  <th><input type='checkbox' @input='selectAll' /></th>
                  <th></th>
                  <th v-show="enabledColumns['name']">
                    {{ $t('folders.list.folder_name') }}
                  </th>
                  <th v-show="enabledColumns['shared']">
                    {{ $t('folders.list.shared') }}
                  </th>
                  <th v-show="enabledColumns['completeness']">
                    {{ $t('folders.list.checklist_status') }}
                  </th>
                  <th v-show="enabledColumns['subscribed']">
                    {{ $t('folders_list.subscribed_folders') }}
                  </th>
                  <th v-show="enabledColumns['tags']">{{ $t('records_list.table.tags') }}</th>
                  <th v-show="enabledColumns['noFilesFolders']">
                    {{ $t('folders_list.no_files_folders') }}
                  </th>
                  <th v-show="enabledColumns['created_at']">
                    {{ $t('folders.list.created_at') }}
                  </th>
                  <th v-show="enabledColumns['actions']">
                    {{ $t('folders.list.actions') }}
                  </th>
                </tr>
                </thead>

                <tbody>
                <tr :key="item.id" v-for='item in folders'>
                  <td class="bulk-check">
                    <input type='checkbox' v-model='bulkOpFolders' :value='item' />
                  </td>
                  <td class="data-img">
                    <router-link
                      :to="{ name: 'folder', params: { id: item.id } }">
                      <img :src="mimeToSrc(item.mime_type)"/>
                    </router-link>
                  </td>
                  <td v-show="enabledColumns['name']">
                    <router-link
                      :to="{ name: 'folder', params: { id: item.id } }">
                      {{ item.name }}
                    </router-link>

                    <a @click.prevent="toggleStar(item)" class="starred"
                       href='#'>
                      <i :class="['fa-star', {'far': !item.starred, 'fas': item.starred }]"></i>
                    </a>
                  </td>
                  <td v-show="enabledColumns['shared']">
                    {{ item.shared ? $t('folders.list.folder_shared') :
                    $t('folders.list.folder_not_shared') }}
                  </td>
                  <td v-show="enabledColumns['completeness']">
                    <checklist-status v-if='folderStats[item.id]'
                      :completed-items-count='folderStats[item.id].checked_checklist_items'
                      :items-count='folderStats[item.id].total_checklist_items'
                    />
                  </td>
                  <td v-show="enabledColumns['subscribed']">
                    {{ isSubscribedTo(item) ? $t('folders_list.yes_way') :
                                              $t('folders_list.no_way') }}
                  </td>
                  <td v-show="enabledColumns['tags']">
                    <folder-tag-manager :folder='item' @success='loadFolders()' />
                  </td>
                  <td v-show="enabledColumns['noFilesFolders']">
                    <div>{{ $t('folders_list.files') }} {{ folderStats[item.id] && folderStats[item.id].records_count }}</div>
                    <div>{{ $t('folders_list.folder') }} {{ folderStats[item.id] && folderStats[item.id].folders_count }}</div>
                  </td>
                  <td v-show="enabledColumns['created_at']">
                    {{ formatDate(item.created_at) }}
                  </td>
                  <td class="dropdown-column" v-show="enabledColumns['actions']">
                    <div class="dropdown">
                      <button aria-expanded="false" aria-haspopup="true"
                              class="btn dropdown-toggle" data-toggle="dropdown"
                              type="button">· · ·
                      </button>
                      <div class="dropdown-menu dropdown-menu-right">
                        <ul>
                          <li>
                            <a @click.prevent="$refs.subscribeFolderForm.open(item)"
                               class="dropdown-item" href="#">
                              <i class="fas fa-folder-plus"></i>
                              <span v-if='isSubscribedTo(item)'>{{ $t('folder_show.activities.unsubscribe_from_folder') }}</span>
                              <span v-else>{{ $t('folder_show.activities.subscribe_to_folder') }}</span>
                            </a>
                          </li>

                          <li>
                            <a @click.prevent='$refs.uploadForm.open(item)'
                               class="dropdown-item" href="#">
                              <i class="fas fa-upload"></i>
                              {{ $t('upload_files_modal.upload_files') }}
                            </a>
                          </li>

                          <li>
                            <a @click.prevent="$refs.folderForm.open({}, item)"
                               class="dropdown-item" href="#">
                              <i class="fas fa-plus"></i>
                              {{ $t('folders.list.add_folder') }}
                            </a>
                          </li>

                          <li>
                            <a @click.prevent='$refs.folderSettings.open(item)'
                               class="dropdown-item" href="#">
                              <i class="fas fa-file"></i>
                              {{ $t('folders_list.file_collection') }}
                            </a>
                          </li>

                          <li>
                            <a @click.prevent='$refs.folderForm.open(item)'
                               class="dropdown-item" href="#">
                              <i class="far fa-edit"></i>
                              {{ $t('root.actions.edit') }}
                            </a>
                          </li>

                          <li>
                            <a @click.prevent='$refs.shareFolderForm.openModal(item.id)'
                               class="dropdown-item" href="#">
                              <i class="fas fa-share-alt"></i>
                              {{ $t('share_invite.share_folder') }}
                            </a>
                          </li>

                          <li>
                            <a @click.prevent='$refs.deleteFolderForm.open(item)'
                               class="dropdown-item" href="#"
                               rel="nofollow">
                              <i class="far fa-trash-alt"></i>
                              {{ $t('root.actions.delete') }}
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <pagination :meta='meta'/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import _modal from '../components/_modal'
  import _loading from '../components/_loading'
  import _pagination from '../components/_pagination'

  import _folder_form from '../components/shared/folders/_folder_form'
  import _folder_settings from '../components/shared/folders/_settings'
  import _delete_folder_form from '../components/shared/folders/_delete_folder_form'
  import _upload_to_folder_form from '../components/shared/folders/_upload_form'
  import _share_folder_form from '../components/folders/_sharing_modal'
  import _bulk_share_folder_form from '../components/folders/_bulk_sharing_modal'
  import _subscribe_to_folder_form from '../components/folders/_subscription_modal'
  import _folder_tag_manager from "../components/tags/_folder_tag_manager"
  import _bulk_tag_items_modal from '../components/shared/items/_bulk_tag_items_modal'
  import _bulk_subscription_form from '../components/folders/_bulk_subscription_form'

  import tag_manager from '../components/tags/_tags_manager'
  import checklistStatus from '../components/contacts/_checklist_status'

  import _contact_form from '../components/shared/contacts/_contact_form'

  import _filter_list from '../components/shared/items/filters/_filter_list_for_contacts'
  import _filter_list_from_params from '../components/shared/items/filters/_filter_list_from_params'

  export default {
    components: {
      modal: _modal,
      loading: _loading,
      pagination: _pagination,
      'tag-manager': tag_manager,
      'folder-form': _folder_form,
      'contact-form': _contact_form,
      'folder-settings': _folder_settings,
      'checklist-status': checklistStatus,
      'share-folder-form': _share_folder_form,
      'bulk-share-folder-form': _bulk_share_folder_form,
      'delete-folder-form': _delete_folder_form,
      'bulk-tag-items-modal': _bulk_tag_items_modal,
      'upload-to-folder-form': _upload_to_folder_form,
      'subscribe-to-folder-form': _subscribe_to_folder_form,
      'bulk-subscription-form': _bulk_subscription_form,
      'folder-tag-manager': _folder_tag_manager,
      'filter-list': _filter_list,
      'filter-list-from-params': _filter_list_from_params,
    },
    props: ['id'],
    created () {
      this.loadContact()
      this.loadFolders()
      this.store.sendMetrics(
        { name: 'contact_folders', params: this.$route.params, path: this.$route.fullPath })
    },
    watch: {
      id () {
        this.loadContact()
        this.loadFolders()
      },
      query () {
        this.loadFolders()
      },
    },
    data () {
      return {
        modalContact: {},
        loading: true,
        showColumnsDropdown: false,
        bulkOpFolders: [],
      }
    },
    computed: {
      userDefaults () {
        return this.currentUser.defaults || {}
      },
      itemColumns () {
        return this.userDefaults['contact'] || []
      },
      enabledColumns () {
        const cols = {}
        this.itemColumns.forEach(c => cols[c] = true)
        return cols
      },
      contacts () {
        return this.lists.contacts || []
      },
      folders () {
        return this.state.items || []
      },
      folderStats () {
        if (!this.stats.folders)
          return {}

        const stats = {}
        this.stats.folders.forEach(f => stats[f.id] = f)
        return stats
      },
      contactStats () {
        if (!this.stats.contacts)
          return {}

        return this.stats.contacts[0]
      },
      checklistItems () {
        return Object.values(this.folderStats).reduce((s, f) => s + f.total_checklist_items, 0)
      },
      checkedItems () {
        return Object.values(this.folderStats).reduce((s, f) => s + f.checked_checklist_items, 0)
      },
      contact () {
        return this.state.contact || {}
      },
      inboxes () {
        return this.contact.inboxes || []
      },
      lastMessage () {
        const documentRequests = this.inboxes.filter(
          i => i && i.interaction_type === 'DocumentRequest').map(i => i.interaction)

        return documentRequests[documentRequests.length - 1]
      },
      lastMessageSrc () {
        if (!this.lastMessage)
          return require('../assets/gray-env.png')

        switch (this.lastMessage.status) {
          case 'pending':
            return require('../assets/yellow-env.png')
          case 'failed':
            return require('../assets/red-env.png')
          case 'delivered':
          case 'opened':
            return require('../assets/green-env.png')
        }
      },
      lastMessageAlt () {
        if (!this.lastMessage)
          return this.$t('contacts.no_sent_message')

        const d = this.formatDate(this.lastMessage.created_at)
        const t = new Date(this.lastMessage.created_at).toLocaleTimeString(this.locale,
          { timeStyle: 'short' })
        switch (this.lastMessage.status) {
          case 'pending':
            return this.$t('contacts.sending_message', { date: d, time: t })
          case 'failed':
            return this.$t('contacts.message_not_sent', { date: d, time: t })
          case 'delivered':
          case 'opened':
            return this.$t('contacts.message_sent', { date: d, time: t })
        }
      },
      contactPageUrl() {
        return window.location.origin + this.$router.resolve({ name: 'public_contact_page', params: { uuid: this.contact.uuid } }).href
      },
    },
    methods: {
      loadFolders () {
        this.bulkOpFolders = []
        const params = Object.assign({ 'filters[klass_eq]': 'Folder', 'filters[contact_id_eq]': this.$props.id, parent_id: 'root', page: 1 }, this.query)

        this.loading = true
        this.store.getItems(params).then(() => {
          this.statStore.getFolders({ 'filters[id]': this.folders.map(x => x.id) })
          this.loading = false
        })
      },
      loadContact () {
        this.store.getContact(this.$props.id)
        this.statStore.getContacts({ 'filters[id]': this.$props.id })
      },
      updateContact (event) {
        this.store.updateContact(this.contact.id, new FormData(event.target)).then(() => {
          this.loadContact()
          this.$refs.contactForm.close()
          this.store.sendMetrics({
            name: 'update_contact',
            params: this.$route.params,
            path: this.$route.fullPath,
          })
        })
      },
      removeConnectedTag (tag) {
        this.store.removeConnectedTag(tag.id, { contact_id: this.contact.id }).then(() => {
          this.store.sendMetrics({
            name: 'remove_tag_from_contact',
            params: this.$route.params,
            path: this.$route.fullPath,
          })

          this.loadContact()
        })
      },
      isSubscribedTo (folder) {
        const subs = folder.subscriptions || []

        return subs.some(s => s.user_id === this.currentUser.id)
      },
      closeShowColumnsDropdown () {
        this.showColumnsDropdown = false
      },
      toggleStar (folder) {
        let promise = null
        promise = this.store.updateFolder(folder.id, { starred: !folder.starred })
        promise.then(() => this.loadFolders())
        this.store.sendMetrics({
          name: 'toggle_folder_star',
          params: this.$route.params,
          path: this.$route.fullPath,
        })
      },
      toggleColumns (column) {
        const newColumns = new Set(this.itemColumns)
        this.enabledColumns[column] ? newColumns.delete(column) : newColumns.add(column)

        const params = {
          user: {
            defaults: Object.assign({}, this.userDefaults,
              { contact: Array.from(newColumns) }),
          },
        }

        this.store.updateUserProfile(params).then(() => {
          this.store.getCurrentUser()
        })
      },
      selectAll(e) {
        this.bulkOpFolders = e.target.checked ? this.folders.slice() : []
      },
    },
  }
</script>

<style lang='sass'>
  @import '../styles/checklists'
  @import '../styles/contact'
  .bulk-container
    background-color: #EBF4FE
    display: flex
    align-items: center
    padding: 8px 0
    border-radius: 3px
    float: left
    width: 100%
    margin-top: 20px
    > span
      padding-left: 10px
    ul
      list-style: none
      padding: 0
      margin: 0
      display: flex
      li
        position: relative
        a
          &:hover, &:visited, &:focus
            background: none
            text-decoration: underline
        .bulk-tags-wrapper
          position: absolute
          top: 0
          right: 0
    &.hide
      display: none
  .completeness-bar
    .progress
      width: 75%
      height: 40px
      text-align: center
      background: #eee
      -webkit-box-shadow: 0 2px 5px rgba(0,0,0,.25) inset
      box-shadow: inset 0 2px 5px rgba(0,0,0,.25)
      float: left
      .bg-success
        padding-left: 0
        text-shadow: -1px -1px 1px hsla(0,0%,100%,.1), 1px 1px 1px rgba(0,0,0,.2)
        background: #567ae3!important
      .empty-checklist
        width: 100%
        color: #686868
        background: #c4c4c4
</style>
